/**
 * useR0006Access Hook
 * Manages role-based access control for R0006 report
 */

import { useMemo } from 'react';
import { useUser } from 'components/RDAuthContext';
import {
  canAccessR0006,
  filterAreasByAccess,
  filterStoresByAccess,
  getAccessLevelMessage,
  hasFullAccess
} from '../config/accessControl';

export default function useR0006Access(areasData, storesData) {
  const { user } = useUser();

  // Calculate access permissions
  const accessInfo = useMemo(() => {
    if (!user) {
      return {
        hasAccess: false,
        hasFullAccess: false,
        accessLevel: 'NO_ACCESS',
        accessMessage: 'User not authenticated',
        filteredAreas: [],
        filteredStores: [],
        userArea: null,
        userRole: null
      };
    }

    const canAccess = canAccessR0006(user);
    const fullAccess = hasFullAccess(user.role);
    
    // Filter areas and stores based on access
    // Note: We need to pass stores to filterAreasByAccess for GenMgr logic
    const filteredAreas = canAccess ? filterAreasByAccess(areasData, user, storesData) : [];
    const filteredStores = canAccess ? filterStoresByAccess(storesData, user, areasData) : [];
    
    // Log access control results
    console.log('🔐 R0006 Access Control Results:', {
      userRole: user.role,
      hasFullAccess: fullAccess,
      canAccess,
      filteredAreasCount: filteredAreas.length,
      filteredAreas: filteredAreas.map(a => a.areaName || a.name),
      filteredStoresCount: filteredStores.length,
      filteredStores: filteredStores.map(s => s.shortName || s.name),
      originalAreasCount: areasData?.length || 0,
      originalStoresCount: storesData?.length || 0
    });
    
    // Get access message with context of accessible areas
    const accessMessage = getAccessLevelMessage(user, filteredAreas);
    
    // Find user's assigned area if they have restricted access
    let userArea = null;
    if (!fullAccess && user.area && filteredAreas.length > 0) {
      userArea = filteredAreas[0]; // Should only have one area for restricted users
    }

    return {
      hasAccess: canAccess,
      hasFullAccess: fullAccess,
      accessLevel: fullAccess ? 'FULL_ACCESS' : (canAccess ? 'AREA_RESTRICTED' : 'NO_ACCESS'),
      accessMessage,
      filteredAreas,
      filteredStores,
      userArea,
      userRole: user.role,
      userName: user.displayName || user.userName
    };
  }, [user, areasData, storesData]);

  // Helper function to check if a specific area is accessible
  const canAccessArea = (areaId) => {
    if (!accessInfo.hasAccess) return false;
    if (accessInfo.hasFullAccess) return true;
    
    return accessInfo.filteredAreas.some(area => 
      area.id === areaId || area.name === areaId
    );
  };

  // Helper function to check if a specific store is accessible
  const canAccessStore = (storeId) => {
    if (!accessInfo.hasAccess) return false;
    if (accessInfo.hasFullAccess) return true;
    
    const hasAccess = accessInfo.filteredStores.some(store => 
      store.id === storeId || 
      store.name === storeId || 
      store.shortName === storeId
    );
    
    if (!hasAccess && storeId) {
      console.log(`🔍 Store access check failed for: "${storeId}"`);
      console.log('Available stores:', accessInfo.filteredStores.map(s => ({
        id: s.id,
        name: s.name,
        shortName: s.shortName
      })));
    }
    
    return hasAccess;
  };

  // Filter report data based on access
  const filterReportData = (reportData, viewType = 'aggregated') => {
    if (!reportData || !accessInfo.hasAccess) return null;
    if (accessInfo.hasFullAccess) return reportData;

    console.log('🔍 Filtering report data for restricted user, viewType:', viewType);
    
    // For area-restricted users, show ALL stores in their accessible areas
    // This gives them the full picture of the areas they're responsible for
    console.log('🏪 Showing all stores in accessible areas');
    
    const filteredData = { ...reportData };
    
    // Get all store names from accessible areas
    const accessibleAreaStoreNames = new Set();
    accessInfo.filteredAreas.forEach(area => {
      if (area.stores && Array.isArray(area.stores)) {
        area.stores.forEach(store => {
          accessibleAreaStoreNames.add(store.shortName || store.name);
        });
      }
    });
    
    console.log('🔍 Accessible area stores:', Array.from(accessibleAreaStoreNames));
    
    // Filter restaurants data
    if (filteredData.restaurants) {
      const originalCount = filteredData.restaurants.length;
      filteredData.restaurants = filteredData.restaurants.filter(item => {
        const storeName = item.store || item.storeName;
        const canAccess = accessibleAreaStoreNames.has(storeName);
        if (!canAccess) {
          console.log(`❌ Filtering out store (not in accessible areas): ${storeName}`);
        }
        return canAccess;
      });
      console.log(`Filtered restaurants: ${originalCount} -> ${filteredData.restaurants.length}`);
    }

    // Filter sales performance data
    if (filteredData.salesPerformance) {
      const originalCount = filteredData.salesPerformance.length;
      filteredData.salesPerformance = filteredData.salesPerformance.filter(item => {
        // Check if this is area-level or store-level data
        if (item.area) {
          return canAccessArea(item.area);
        }
        if (item.store || item.storeName) {
          return canAccessStore(item.store || item.storeName);
        }
        return false;
      });
      console.log(`Filtered salesPerformance: ${originalCount} -> ${filteredData.salesPerformance.length}`);
    }

    // Filter labor hours data using the same accessible area stores approach
    if (filteredData.laborHours) {
      const originalCount = filteredData.laborHours.length;
      
      // Get accessible area store names (reuse the same logic)
      const accessibleAreaStoreNames = new Set();
      accessInfo.filteredAreas.forEach(area => {
        if (area.stores && Array.isArray(area.stores)) {
          area.stores.forEach(store => {
            accessibleAreaStoreNames.add(store.shortName || store.name);
          });
        }
      });
      
      filteredData.laborHours = filteredData.laborHours.filter(item => {
        // Labor hours data structure: { area: storeName, areaName: actualAreaName }
        // So item.area is actually the store name, not the area name
        const storeName = item.area || item.store || item.storeName;
        const actualAreaName = item.areaName;
        
        console.log('🔍 Labor hours item filtering:', {
          storeName: storeName,
          actualAreaName: actualAreaName,
          accessibleStores: Array.from(accessibleAreaStoreNames).slice(0, 5),
          isStoreAccessible: accessibleAreaStoreNames.has(storeName)
        });
        
        if (storeName) {
          // Check if this store is in the accessible stores
          const isAccessible = accessibleAreaStoreNames.has(storeName);
          console.log(`  ${isAccessible ? '✅' : '❌'} Store ${storeName}: ${isAccessible ? 'accessible' : 'filtered out'}`);
          return isAccessible;
        }
        
        console.log('  ❌ No store name found, filtering out');
        return false;
      });
      console.log(`Filtered laborHours: ${originalCount} -> ${filteredData.laborHours.length}`);
    }

    // Filter KPI data
    if (filteredData.kpiData) {
      // For area-restricted users, only show their area's KPIs
      if (accessInfo.userArea && !accessInfo.hasFullAccess) {
        filteredData.kpiData = {
          ...filteredData.kpiData,
          areaName: accessInfo.userArea.name
        };
      }
    }

    // Recalculate totals based on filtered data
    if (filteredData.restaurants && filteredData.restaurants.length > 0) {
      filteredData.totalSales = filteredData.restaurants.reduce((sum, r) => sum + (r.actSales || 0), 0);
      filteredData.opportunity = filteredData.restaurants.reduce((sum, r) => sum + (r.opportunity || 0), 0);
    }
    
    // Recalculate labor hours totals if needed
    if (filteredData.laborHours && filteredData.laborHours.length > 0 && filteredData.laborHoursTotals) {
      // This would need the calculateLaborHoursTotals function from laborHoursService
      // For now, we'll just note that totals need recalculation
      console.log('⚠️ Labor hours totals may need recalculation after filtering');
    }
    
    return filteredData;
  };

  return {
    ...accessInfo,
    canAccessArea,
    canAccessStore,
    filterReportData
  };
}